import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, ViewEncapsulation, Input, Output, ContentChildren, ViewChild, Directive, NgModule } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CheckboxComponent, CheckboxModule } from '@appkit4/angular-components/checkbox';
import { PaginationModule } from '@appkit4/angular-components/pagination';
import cloneDeep from 'lodash-es/cloneDeep';
import { convertToRem } from '@appkit4/angular-components/utils';
import * as i1 from '@appkit4/angular-api';

class SortTableService {
    constructor() {
        this.sortEvent = new Subject();
        this.sortedData = {};
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: SortTableService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: SortTableService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: SortTableService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });
class TableComponent {
    set originalData(value) {
        this._originalData = value;
        if (this.hasSorter) {
            if (this.allowDataReload) {
                this.initData();
                this.tableDataReload.emit(this._originalData);
                if (!this.disableDefaultSort) {
                    this.sortTableService.sortEvent.next({ phase: 0, tableId: this.tableId });
                }
                this.initPagination();
                setTimeout(() => {
                    this.loadTableRows();
                    this.loadHeights();
                }, 0);
                if (this.checkboxNodes.length && this.selectAllref) {
                    this.triggerAllCheckboxChange = true;
                    this.selectAllref.instance.setCheckboxState({
                        originEvent: null,
                        checked: false
                    });
                }
            }
            else {
                this.allowDataReload = true;
            }
        }
        else {
            this.data = value;
            this.dataChange.emit(this.data);
            setTimeout(() => {
                this.loadTableRows();
                this.setStrips();
            }, 0);
        }
    }
    get originalData() {
        return this._originalData;
    }
    set condensed(value) {
        this._condensed = value;
        // reset table
        this.sortTableService.sortEvent.next({ phase: 0, tableId: this.tableId });
        setTimeout(() => {
            this.loadHeights();
        }, 0);
    }
    get condensed() {
        return this._condensed;
    }
    set sortActive(value) {
        this._sortActive = value;
        if (this.sortActive && (this.sortPhase === 0 || this.sortPhase === 1 || this.sortPhase === 2)) {
            this.sortTableService.sortEvent.next({ phase: -1, tableId: this.tableId, sortKey: this.sortActive, sortPhase: this.sortPhase });
        }
    }
    get sortActive() {
        return this._sortActive;
    }
    set sortPhase(value) {
        this._sortPhase = value;
        if (this.sortActive && (this.sortPhase === 0 || this.sortPhase === 1 || this.sortPhase === 2)) {
            this.sortTableService.sortEvent.next({ phase: -1, tableId: this.tableId, sortKey: this.sortActive, sortPhase: this.sortPhase });
        }
    }
    get sortPhase() {
        return this._sortPhase;
    }
    constructor(element, render, sortTableService, resolver, injector, app, cdr) {
        this.element = element;
        this.render = render;
        this.sortTableService = sortTableService;
        this.resolver = resolver;
        this.injector = injector;
        this.app = app;
        this.cdr = cdr;
        this.styleClass = '';
        this.style = '';
        this.tableId = String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now();
        this.config = {};
        this.originalDataChange = new EventEmitter();
        this.onSort = new EventEmitter();
        this.displayDataChange = new EventEmitter();
        this.tableDataReload = new EventEmitter();
        this.disableDefaultSort = false;
        this.animatedSorting = true;
        this.striped = false;
        this.dataChange = new EventEmitter();
        this.showSelectAll = false;
        this.onSelectAllChange = new EventEmitter();
        this._originalData = [];
        this._condensed = false;
        this.allowDataReload = true;
        this.sortedData = [];
        this.hasSorter = false;
        this.allowAnimationWhenReload = true;
        this.pageSize = 5;
        this.startIndex = 0;
        this.endIndex = this.pageSize;
        this.currentIndex = this.startIndex;
        this.trows = []; // NodeList of tr
        this.trowsHeight = []; // Height of each tr, sorted by initial index
        this.trowsHeightSum = []; // Position of each tr, sorted by initial index
        this.trowsSortedHeight = []; // Height of each tr, sorted by sorted index
        this.trowsSortedHeightSum = []; // Position of each tr, sorted by sorted index
        // Properties to support selectAll functionality
        this.triggerAllCheckboxChange = true;
        this.selectAllStatus = false;
    }
    ngOnInit() {
        if (this.disableDefaultSort) {
            this.animatedSorting = false;
        }
    }
    ngAfterViewInit() {
        this.createHeadContainer();
        this.loadTableRows();
        this.initData();
        this.setSelectAllState();
        setTimeout(() => {
            this.setStrips();
            if (this.animatedSorting) {
                this.loadHeights();
            }
            this.initPagination();
        }, 500);
        if (this.hasSorter && this.animatedSorting) {
            this.resizeObservable$ = fromEvent(window, 'resize');
            this.resizeSubscription$ = this.resizeObservable$.pipe(debounceTime(500)).subscribe((event) => {
                this.loadHeights();
            });
        }
        if (this.sortActive && (this.sortPhase === 0 || this.sortPhase === 1 || this.sortPhase === 2)) {
            this.sortTableService.sortEvent.next({ phase: -1, tableId: this.tableId, sortKey: this.sortActive, sortPhase: this.sortPhase });
        }
        if (this.hasSorter) {
            this.displayDataChange.subscribe((changedData) => {
                this.loadTableRows();
                setTimeout(() => {
                    this.setStrips();
                    if (this.animatedSorting) {
                        this.loadHeights();
                    }
                }, 0);
                this.sortedData = cloneDeep(changedData);
                if (!this.disableDefaultSort && this.animatedSorting && this.allowAnimationWhenReload) {
                    // Initialize trowsSortedHeight everytime data is sorted
                    this.sortedData.forEach((data, i) => {
                        this.trowsSortedHeight[i] = this.trowsHeight[data._previousSortIndex];
                    });
                    // Initialize trowsSortedHeightSum everytime data is sorted
                    let trowsSortedHeightSum = this.trowsSortedHeight.reduce((a, b, i) => {
                        this.trowsSortedHeightSum[i] = a + b;
                        return a + b;
                    }, 0);
                    this.sortedData.sort((a, b) => { return a._previousSortIndex > b._previousSortIndex ? 1 : -1; });
                    setTimeout(() => {
                        this.trows.forEach((th) => {
                            th.classList.remove('striped');
                        });
                        this.sortedData.forEach((rowData, i) => {
                            this.animate(rowData, this.trows[i]);
                            // if (rowData._nextSortIndex % 2 === 0) {
                            //     this.trows[i].classList.add('striped');
                            // }
                        });
                        setTimeout(() => {
                            this.trows.forEach((th) => {
                                th.style.transition = 'background-color 0.3s, transform 0s';
                                th.style.transform = 'none';
                            });
                            this.data = cloneDeep(this.sortedData);
                            this.data.sort((a, b) => { return a._nextSortIndex > b._nextSortIndex ? 1 : -1; });
                            this.dataChange.emit(this.data);
                            setTimeout(() => {
                                this.loadTableRows();
                                this.loadHeights();
                                this.setStrips();
                            }, 0);
                        }, 300); // wait for the transition time of trows
                    }, 0);
                }
                else {
                    this.allowAnimationWhenReload = true;
                    setTimeout(() => {
                        this.data = this.config.pagination ? this.sortedData.slice(this.startIndex, this.endIndex) : cloneDeep(this.sortedData);
                        this.dataChange.emit(this.data);
                        setTimeout(() => {
                            this.loadTableRows();
                            this.setStrips();
                        }, 0);
                    }, 300); // wait for the transition time of sort icon
                }
            });
        }
        if (this.checkboxNodes.length) {
            this.checkboxNodes.changes.subscribe((change) => {
                this.setSelectAllState();
            });
        }
    }
    ngOnDestroy() {
        if (this.hasSorter && this.animatedSorting && this.resizeSubscription$) {
            this.resizeSubscription$.unsubscribe();
        }
        if (this.config.pagination && this.pagination) {
            this.pagination.onChange.unsubscribe();
        }
    }
    ngOnChanges(changes) {
        if (changes.showSelectAll && !changes.showSelectAll.firstChange && this.selectAllref) {
            const seletcAllEle = this.selectAllref.instance.el.nativeElement.querySelector('.ap-checkbox-container');
            if (seletcAllEle) {
                if (this.showSelectAll) {
                    seletcAllEle.classList.add('show');
                }
                else {
                    seletcAllEle.classList.remove('show');
                }
            }
        }
    }
    loadTableRows() {
        const tbody = this.element.nativeElement.querySelector('tbody');
        this.trows = [...tbody.getElementsByTagName('tr')];
    }
    loadHeights() {
        // Initialize trowsHeight
        this.trows.forEach((row, i) => {
            this.trowsHeight[i] = row.getBoundingClientRect().height;
        });
        // Initialize trowsHeightSum
        let trowsHeightSum = this.trowsHeight.reduce((a, b, i) => {
            this.trowsHeightSum[i] = a + b;
            return a + b;
        }, 0);
    }
    setStrips() {
        this.trows.forEach((tr, i) => {
            if (i % 2 === 0) {
                tr.classList.add('striped');
            }
        });
    }
    animate(rowData, element, reverse) {
        // calculate the distance each row moves
        let val = reverse ? 0 : (this.trowsSortedHeightSum[rowData._nextSortIndex] - this.trowsHeightSum[rowData._previousSortIndex] + 4 * (rowData._nextSortIndex - rowData._previousSortIndex));
        element.style.transform = `translateY(${convertToRem(val)})`;
        element.style.transition = 'background-color 0.3s, transform 0.3s';
    }
    ;
    initData() {
        if (!this.originalData) {
            this.originalData = [];
        }
        this.data = this.data || cloneDeep(this.originalData);
        if (!this.disableDefaultSort) {
            this.sortedData = [];
            this.originalData.forEach((data, i) => {
                this.sortedData.push({
                    _initialSortIndex: i,
                    _previousSortIndex: i,
                    _nextSortIndex: i,
                    ...data
                });
            });
            this.sortTableService.sortedData[this.tableId] = this.sortedData;
        }
        else {
            this.sortedData = cloneDeep(this.originalData);
        }
    }
    htmlToElement(html) {
        let template = this.render.createElement('template');
        template.innerHTML = html;
        return template.content;
    }
    createHeadContainer() {
        const table = this.apTable.nativeElement.querySelector('table');
        if (!table?.querySelector('thead')) {
            return;
        }
        const th = table.getElementsByTagName('th');
        for (let i = 0; i < th.length; i++) {
            let container = this.render.createElement('div');
            container.classList.add('header-container');
            if (i === 0) {
                let factory = this.resolver.resolveComponentFactory(CheckboxComponent);
                this.selectAllref = factory.create(this.injector, [], container);
                this.selectAllref.instance.onChange.subscribe((event) => {
                    if (this.selectAllStatus !== event.checkboxState) {
                        if (this.showSelectAll) {
                            this.onSelectAllChange.emit(event);
                        }
                        this.selectAllStatus = event.checkboxState;
                    }
                    if (this.triggerAllCheckboxChange) {
                        this.checkboxNodes.forEach((checkboxRef) => {
                            checkboxRef.setCheckboxState({
                                originEvent: event.originEvent,
                                checked: event.checkboxState
                            });
                        });
                    }
                    else {
                        this.triggerAllCheckboxChange = true;
                    }
                });
                this.app.attachView(this.selectAllref.hostView);
                this.render.listen(this.selectAllref.instance.el.nativeElement.querySelector('.ap-checkbox-container'), 'click', (event) => {
                    event.stopPropagation();
                });
                this.render.listen(this.selectAllref.instance.el.nativeElement.querySelector('.ap-checkbox-container'), 'keydown', (event) => {
                    event.stopPropagation();
                });
            }
            const originalHtml = this.htmlToElement(th[i].innerHTML);
            container.appendChild(originalHtml);
            th[i].innerHTML = '';
            th[i].appendChild(container);
        }
    }
    setSelectAllState() {
        if (!this.selectAllref) {
            return;
        }
        if (this.checkboxNodes.length) {
            let checkboxStates = new Array(this.checkboxNodes.length);
            this.checkboxNodes.forEach((element, i) => {
                setTimeout(() => {
                    checkboxStates[i] = element.checked || false;
                });
                element.onChange.subscribe((event) => {
                    checkboxStates[i] = event.checkboxState;
                    if (event.checkboxState === false && this.selectAllref.instance.checked) {
                        this.triggerAllCheckboxChange = false;
                        this.selectAllref.instance.setCheckboxState({
                            originEvent: event.originEvent,
                            checked: false
                        });
                    }
                    if (checkboxStates.findIndex((value => value === false)) === -1) {
                        this.triggerAllCheckboxChange = false;
                        this.selectAllref.instance.setCheckboxState({
                            originEvent: event.originEvent,
                            checked: true
                        });
                    }
                });
            });
            setTimeout(() => {
                if (checkboxStates.findIndex((value => value === false)) === -1) {
                    this.triggerAllCheckboxChange = false;
                    this.selectAllref.instance.setCheckboxState({
                        originEvent: new Event('click'),
                        checked: true
                    });
                }
            });
        }
        const seletcAllEle = this.selectAllref?.instance.el.nativeElement.querySelector('.ap-checkbox-container');
        if (seletcAllEle) {
            if (this.showSelectAll) {
                seletcAllEle.classList.add('show');
            }
            else {
                seletcAllEle.classList.remove('show');
            }
        }
    }
    initPagination() {
        if (this.config.pagination) {
            this.animatedSorting = false;
            this.pagination = this.config.pagination;
            this.pageSize = this.config.pageSize || 50;
            this.pagination.max = Math.ceil(this.originalData.length / this.pageSize);
            // When the source data is updated, reset currentIndex if it exceeds the max page limit.
            if (this.pagination.max < this.currentIndex + 1) {
                this.currentIndex = 0;
                this.pagination.value = this.currentIndex + 1;
                this.startIndex = this.currentIndex;
            }
            else {
                this.currentIndex = this.pagination.value - 1;
                this.startIndex = this.currentIndex * this.pageSize;
            }
            this.endIndex = this.startIndex + this.pageSize;
            this.data = this.originalData.slice(this.startIndex, this.endIndex);
            this.dataChange.emit(this.data);
            this.pagination.onChange.subscribe((event) => {
                this.currentIndex = event - 1;
                this.startIndex = (event - 1) * this.pageSize;
                this.endIndex = this.startIndex + this.pageSize;
                this.data = this.sortedData.slice(this.startIndex, this.endIndex);
                this.dataChange.emit(this.data);
                setTimeout(() => {
                    this.loadTableRows();
                    this.setStrips();
                }, 0);
                if (this.checkboxNodes.length && this.selectAllref) {
                    this.triggerAllCheckboxChange = true;
                    this.selectAllref.instance.setCheckboxState({
                        originEvent: null,
                        checked: false
                    });
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: TableComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: SortTableService }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.11", type: TableComponent, selector: "ap-table", inputs: { styleClass: "styleClass", style: "style", tableId: "tableId", config: "config", originalData: "originalData", disableDefaultSort: "disableDefaultSort", animatedSorting: "animatedSorting", striped: "striped", condensed: "condensed", data: "data", sortActive: "sortActive", sortPhase: "sortPhase", showSelectAll: "showSelectAll" }, outputs: { originalDataChange: "originalDataChange", onSort: "onSort", displayDataChange: "displayDataChange", tableDataReload: "tableDataReload", dataChange: "dataChange", onSelectAllChange: "onSelectAllChange" }, queries: [{ propertyName: "checkboxNodes", predicate: CheckboxComponent, descendants: true }], viewQueries: [{ propertyName: "apTable", first: true, predicate: ["apTable"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
        <div #apTable class="ap-table {{styleClass}}" style="{{style}}" [id]="tableId" [class.ap-table-condensed]="condensed" [class.ap-table-striped]="striped">
            <ng-content></ng-content>
        </div>
    `, isInline: true, styles: [".animated{-webkit-animation-duration:.3s;animation-duration:.3s;-webkit-animation-fill-mode:both;animation-fill-mode:both}.rotateIn{-webkit-animation-name:rotateIn;animation-name:rotateIn}@keyframes rotateIn{0%{-webkit-transform-origin:center;transform-origin:center;-webkit-transform:rotate(180deg);transform:rotate(180deg);opacity:1}to{-webkit-transform-origin:center;transform-origin:center;-webkit-transform:rotate(0);transform:rotate(0);opacity:1}}.rotateOut{-webkit-animation-name:rotateOut;animation-name:rotateOut}@keyframes rotateOut{0%{-webkit-transform-origin:center;transform-origin:center;-webkit-transform:rotate(0);transform:rotate(0);opacity:1}to{-webkit-transform-origin:center;transform-origin:center;-webkit-transform:rotate(180deg);transform:rotate(180deg);opacity:1}}.ap-table{width:100%;padding:var(--spacing-2) var(--spacing-3);box-shadow:inset 0 0 0 .0625rem var(--color-background-border);border-radius:var(--border-radius-3);background-color:var(--color-background-container);letter-spacing:-.025rem}.ap-table table{border-spacing:0 var(--spacing-2);width:100%;font-size:1rem;border-collapse:separate}.ap-table table thead th{line-height:1.5rem;font-size:1rem;font-weight:var(--font-weight-2);color:var(--color-text-heading);transition:background-color .3s;padding:0}.ap-table table thead th:not(:last-child) .header-container{margin-right:var(--spacing-2)}.ap-table table thead th .header-container{display:flex;justify-content:space-between;column-gap:var(--spacing-3);height:3rem;line-height:1.5rem;padding:0 var(--spacing-4);margin-bottom:var(--spacing-2);border-radius:var(--border-radius-2);align-items:center}.ap-table table thead th .header-container .Appkit4-icon.sort-table{color:var(--color-text-heading);font-size:.75rem;opacity:1;line-height:1.5rem}.ap-table table thead th .header-container .Appkit4-icon.sort-table:before{font-weight:var(--font-weight-2)}.ap-table table thead th .header-container .Appkit4-icon.sort-table.hidden{display:none}.ap-table table thead th .header-container .Appkit4-icon.sort-table.sorted{color:var(--color-background-icon-primary)}.ap-table table thead th .header-container .Appkit4-icon.sort-table.sorted.descendant{transform:rotate(180deg)}.ap-table table thead th .header-container .ap-checkbox-container{margin-right:calc(-1 * var(--spacing-2))!important}.ap-table table thead th .header-container .ap-checkbox-container:not(.show){display:none}.ap-table table thead th .header-container .sort-icon-wrapper{width:2.125rem;height:2.125rem;display:flex;align-items:center;justify-content:center}.ap-table table thead th .header-container:hover .Appkit4-icon.sort-table:not(.sorted),.ap-table table thead th .header-container .sort-icon-wrapper:focus-within .Appkit4-icon.sort-table:not(.sorted){color:var(--color-background-icon-primary)}.ap-table table thead th .header-container.has-sorter:hover{cursor:pointer;background-color:var(--color-background-hover)}.ap-table table thead th .header-container.has-sorter .ap-table-header-description-sr-only{display:none}.ap-table table thead th .header-container.head-sorted{background-color:var(--color-background-hover)}.ap-table table thead th .header-container.start-slot{flex-direction:row-reverse}.ap-table table thead th .header-container.start-slot>*{margin-left:auto}.ap-table table thead th .header-container.start-slot .Appkit4-icon.sort-table{padding-right:0;transition:opacity .3s,padding-right .3s,color .3s}.ap-table table thead th .header-container.start-slot .sort-icon-wrapper{margin-left:-.3125rem}.ap-table table thead th .header-container.start-slot:not(:hover) .sort-icon-wrapper:not(:focus-within) .Appkit4-icon.sort-table:not(.sorted){padding-right:var(--spacing-3);opacity:0}.ap-table table thead th .header-container.end-slot{flex-direction:row}.ap-table table thead th .header-container.end-slot>*{margin-right:auto}.ap-table table thead th .header-container.end-slot .Appkit4-icon.sort-table{padding-left:0;transition:opacity .3s,padding-left .3s,color .3s}.ap-table table thead th .header-container.end-slot .sort-icon-wrapper{margin-right:-.3125rem}.ap-table table thead th .header-container.end-slot:not(:hover) .sort-icon-wrapper:not(:focus-within) .Appkit4-icon.sort-table:not(.sorted){padding-left:var(--spacing-3);opacity:0}.ap-table table tbody tr{padding:0;height:3rem;color:var(--color-text-body);transition:background-color .3s,transform .3s}.ap-table table tbody tr.selected,.ap-table table tbody tr:hover{background-color:var(--color-background-hover)}.ap-table table tbody tr td{line-height:1.5rem;padding:var(--spacing-4)}.ap-table table tbody tr td:first-child{border-top-left-radius:var(--border-radius-2);border-bottom-left-radius:var(--border-radius-2)}.ap-table table tbody tr td:last-child{border-top-right-radius:var(--border-radius-2);border-bottom-right-radius:var(--border-radius-2)}.ap-table table tbody tr td span{vertical-align:inherit}.ap-table table tbody tr td .ap-checkbox-wrapper{vertical-align:inherit;margin:0 var(--spacing-3) 0 0;position:relative}.ap-table table tbody tr td .ap-checkbox-wrapper .Appkit4-icon:before{font-weight:var(--font-weight-2)}.ap-table table tbody tr td .ap-checkbox-wrapper .icon-check-blank-outline{color:var(--color-text-body)}.ap-table table tbody tr td .ap-checkbox-wrapper .icon-check-backdrop-outline{display:none;position:relative;color:var(--color-text-primary);font-size:.75rem}.ap-table table tbody tr td .ap-checkbox-wrapper .a-checkbox-text{height:1.5rem;line-height:1.5rem;font-size:.75rem;font-weight:400;color:var(--color-text-body)}.ap-table table tbody tr td .ap-checkbox-wrapper input[type=checkbox]{position:absolute;width:0;height:0;opacity:0}.ap-table table tbody tr td .ap-checkbox-wrapper input[type=checkbox]:checked~.icon-check-blank-outline{display:none}.ap-table table tbody tr td .ap-checkbox-wrapper input[type=checkbox]:checked~.icon-check-backdrop-outline{display:inline-block}.ap-table.ap-table-condensed tbody tr{height:2rem!important}.ap-table.ap-table-condensed tbody tr td{padding-top:var(--spacing-2)!important;padding-bottom:var(--spacing-2)!important}.ap-table.ap-table-striped tbody tr.striped{background-color:var(--color-background-selected)}.ap-table.ap-table-striped tbody tr.striped:hover,.ap-table.ap-table-striped tbody tr.striped.selected{background-color:var(--color-background-hover-selected)}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: TableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ap-table', template: `
        <div #apTable class="ap-table {{styleClass}}" style="{{style}}" [id]="tableId" [class.ap-table-condensed]="condensed" [class.ap-table-striped]="striped">
            <ng-content></ng-content>
        </div>
    `, encapsulation: ViewEncapsulation.None, styles: [".animated{-webkit-animation-duration:.3s;animation-duration:.3s;-webkit-animation-fill-mode:both;animation-fill-mode:both}.rotateIn{-webkit-animation-name:rotateIn;animation-name:rotateIn}@keyframes rotateIn{0%{-webkit-transform-origin:center;transform-origin:center;-webkit-transform:rotate(180deg);transform:rotate(180deg);opacity:1}to{-webkit-transform-origin:center;transform-origin:center;-webkit-transform:rotate(0);transform:rotate(0);opacity:1}}.rotateOut{-webkit-animation-name:rotateOut;animation-name:rotateOut}@keyframes rotateOut{0%{-webkit-transform-origin:center;transform-origin:center;-webkit-transform:rotate(0);transform:rotate(0);opacity:1}to{-webkit-transform-origin:center;transform-origin:center;-webkit-transform:rotate(180deg);transform:rotate(180deg);opacity:1}}.ap-table{width:100%;padding:var(--spacing-2) var(--spacing-3);box-shadow:inset 0 0 0 .0625rem var(--color-background-border);border-radius:var(--border-radius-3);background-color:var(--color-background-container);letter-spacing:-.025rem}.ap-table table{border-spacing:0 var(--spacing-2);width:100%;font-size:1rem;border-collapse:separate}.ap-table table thead th{line-height:1.5rem;font-size:1rem;font-weight:var(--font-weight-2);color:var(--color-text-heading);transition:background-color .3s;padding:0}.ap-table table thead th:not(:last-child) .header-container{margin-right:var(--spacing-2)}.ap-table table thead th .header-container{display:flex;justify-content:space-between;column-gap:var(--spacing-3);height:3rem;line-height:1.5rem;padding:0 var(--spacing-4);margin-bottom:var(--spacing-2);border-radius:var(--border-radius-2);align-items:center}.ap-table table thead th .header-container .Appkit4-icon.sort-table{color:var(--color-text-heading);font-size:.75rem;opacity:1;line-height:1.5rem}.ap-table table thead th .header-container .Appkit4-icon.sort-table:before{font-weight:var(--font-weight-2)}.ap-table table thead th .header-container .Appkit4-icon.sort-table.hidden{display:none}.ap-table table thead th .header-container .Appkit4-icon.sort-table.sorted{color:var(--color-background-icon-primary)}.ap-table table thead th .header-container .Appkit4-icon.sort-table.sorted.descendant{transform:rotate(180deg)}.ap-table table thead th .header-container .ap-checkbox-container{margin-right:calc(-1 * var(--spacing-2))!important}.ap-table table thead th .header-container .ap-checkbox-container:not(.show){display:none}.ap-table table thead th .header-container .sort-icon-wrapper{width:2.125rem;height:2.125rem;display:flex;align-items:center;justify-content:center}.ap-table table thead th .header-container:hover .Appkit4-icon.sort-table:not(.sorted),.ap-table table thead th .header-container .sort-icon-wrapper:focus-within .Appkit4-icon.sort-table:not(.sorted){color:var(--color-background-icon-primary)}.ap-table table thead th .header-container.has-sorter:hover{cursor:pointer;background-color:var(--color-background-hover)}.ap-table table thead th .header-container.has-sorter .ap-table-header-description-sr-only{display:none}.ap-table table thead th .header-container.head-sorted{background-color:var(--color-background-hover)}.ap-table table thead th .header-container.start-slot{flex-direction:row-reverse}.ap-table table thead th .header-container.start-slot>*{margin-left:auto}.ap-table table thead th .header-container.start-slot .Appkit4-icon.sort-table{padding-right:0;transition:opacity .3s,padding-right .3s,color .3s}.ap-table table thead th .header-container.start-slot .sort-icon-wrapper{margin-left:-.3125rem}.ap-table table thead th .header-container.start-slot:not(:hover) .sort-icon-wrapper:not(:focus-within) .Appkit4-icon.sort-table:not(.sorted){padding-right:var(--spacing-3);opacity:0}.ap-table table thead th .header-container.end-slot{flex-direction:row}.ap-table table thead th .header-container.end-slot>*{margin-right:auto}.ap-table table thead th .header-container.end-slot .Appkit4-icon.sort-table{padding-left:0;transition:opacity .3s,padding-left .3s,color .3s}.ap-table table thead th .header-container.end-slot .sort-icon-wrapper{margin-right:-.3125rem}.ap-table table thead th .header-container.end-slot:not(:hover) .sort-icon-wrapper:not(:focus-within) .Appkit4-icon.sort-table:not(.sorted){padding-left:var(--spacing-3);opacity:0}.ap-table table tbody tr{padding:0;height:3rem;color:var(--color-text-body);transition:background-color .3s,transform .3s}.ap-table table tbody tr.selected,.ap-table table tbody tr:hover{background-color:var(--color-background-hover)}.ap-table table tbody tr td{line-height:1.5rem;padding:var(--spacing-4)}.ap-table table tbody tr td:first-child{border-top-left-radius:var(--border-radius-2);border-bottom-left-radius:var(--border-radius-2)}.ap-table table tbody tr td:last-child{border-top-right-radius:var(--border-radius-2);border-bottom-right-radius:var(--border-radius-2)}.ap-table table tbody tr td span{vertical-align:inherit}.ap-table table tbody tr td .ap-checkbox-wrapper{vertical-align:inherit;margin:0 var(--spacing-3) 0 0;position:relative}.ap-table table tbody tr td .ap-checkbox-wrapper .Appkit4-icon:before{font-weight:var(--font-weight-2)}.ap-table table tbody tr td .ap-checkbox-wrapper .icon-check-blank-outline{color:var(--color-text-body)}.ap-table table tbody tr td .ap-checkbox-wrapper .icon-check-backdrop-outline{display:none;position:relative;color:var(--color-text-primary);font-size:.75rem}.ap-table table tbody tr td .ap-checkbox-wrapper .a-checkbox-text{height:1.5rem;line-height:1.5rem;font-size:.75rem;font-weight:400;color:var(--color-text-body)}.ap-table table tbody tr td .ap-checkbox-wrapper input[type=checkbox]{position:absolute;width:0;height:0;opacity:0}.ap-table table tbody tr td .ap-checkbox-wrapper input[type=checkbox]:checked~.icon-check-blank-outline{display:none}.ap-table table tbody tr td .ap-checkbox-wrapper input[type=checkbox]:checked~.icon-check-backdrop-outline{display:inline-block}.ap-table.ap-table-condensed tbody tr{height:2rem!important}.ap-table.ap-table-condensed tbody tr td{padding-top:var(--spacing-2)!important;padding-bottom:var(--spacing-2)!important}.ap-table.ap-table-striped tbody tr.striped{background-color:var(--color-background-selected)}.ap-table.ap-table-striped tbody tr.striped:hover,.ap-table.ap-table-striped tbody tr.striped.selected{background-color:var(--color-background-hover-selected)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: SortTableService }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: i0.ChangeDetectorRef }], propDecorators: { styleClass: [{
                type: Input
            }], style: [{
                type: Input
            }], tableId: [{
                type: Input
            }], config: [{
                type: Input
            }], originalData: [{
                type: Input
            }], originalDataChange: [{
                type: Output
            }], onSort: [{
                type: Output
            }], displayDataChange: [{
                type: Output
            }], tableDataReload: [{
                type: Output
            }], disableDefaultSort: [{
                type: Input
            }], animatedSorting: [{
                type: Input
            }], striped: [{
                type: Input
            }], condensed: [{
                type: Input
            }], data: [{
                type: Input
            }], dataChange: [{
                type: Output
            }], sortActive: [{
                type: Input
            }], sortPhase: [{
                type: Input
            }], showSelectAll: [{
                type: Input
            }], onSelectAllChange: [{
                type: Output
            }], checkboxNodes: [{
                type: ContentChildren,
                args: [CheckboxComponent, { descendants: true }]
            }], apTable: [{
                type: ViewChild,
                args: ['apTable']
            }] } });
class SortTableDirective {
    constructor(element, sortTableService, tableComponent, render, localizationConfig) {
        this.element = element;
        this.sortTableService = sortTableService;
        this.tableComponent = tableComponent;
        this.render = render;
        this.localizationConfig = localizationConfig;
        this.sortKey = '';
        this.slot = 'end';
        this.originData = [];
        this.sortedData = [];
        this.indexedSortedData = [];
        this.sortFlag = 0; // 0: sorter inactive; 1: second sorting phase; 2: first sorting phase; 3: sorter clicked, before first sorting
        this.sorterId = String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now();
        this.tableId = '';
    }
    ngOnInit() {
        this.translationSubscription$ = this.localizationConfig.translationObserver.subscribe(() => {
            const sortPhase = this.sortFlag === 0 ? 0 : this.sortFlag === 2 ? 1 : 2;
            let order = 'ascending';
            if (sortPhase === 0) {
                order = this.sortFunc1 ? 'custom' : 'ascending';
            }
            else if (sortPhase === 1) {
                order = this.sortFunc2 ? 'custom' : 'descending';
            }
            this.setDescription(sortPhase !== 2, order);
        });
        this.tableId = this.tableComponent.tableId;
        if (this.sortKey) {
            this.tableComponent.hasSorter = true;
            this.originData = cloneDeep(this.tableComponent.originalData);
            this.sortedData = cloneDeep(this.tableComponent.originalData);
            this.tableComponent.tableDataReload.subscribe((newData) => {
                this.originData = cloneDeep(newData);
                this.sortedData = cloneDeep(newData);
                if (this.tableComponent.disableDefaultSort) {
                    this.tableComponent.displayDataChange.emit(this.originData);
                }
            });
            this.sortTableService.sortEvent.subscribe((val) => {
                if (val.tableId !== this.tableId) {
                    return;
                }
                if (!this.tableComponent.disableDefaultSort) {
                    this.indexedSortedData = cloneDeep(this.sortTableService.sortedData[this.tableId]);
                }
                if (val.phase === 1) { // one sorter active, reset other sorters
                    this.sortFlag = this.sortFlag === 3 ? 2 : 0;
                }
                else if (val.phase === -1) { // set active sorter at init
                    if (val.sortKey !== this.sortKey) {
                        return;
                    }
                    this.sortFlag = val.sortPhase === 1 ? 0 : val.sortPhase === 2 ? 2 : 1;
                    setTimeout(() => {
                        this.sort();
                    });
                }
                else if (val.phase === 0) { // reset sorter
                    setTimeout(() => {
                        const hoverHead = this.element.nativeElement.querySelector('.header-container');
                        const defaultIcon = this.element.nativeElement.querySelector('.icon-sorting-fill');
                        const sortupIcon = this.element.nativeElement.querySelector('.icon-sorting-ascendant-fill');
                        this.sortFlag = 0;
                        if (sortupIcon) {
                            sortupIcon.classList.remove('sorted', 'descendant');
                            sortupIcon.classList.add('hidden');
                        }
                        if (defaultIcon) {
                            defaultIcon.classList.remove('sorted');
                            defaultIcon.classList.remove('hidden');
                        }
                        hoverHead.classList.remove('head-sorted');
                        this.element.nativeElement.removeAttribute('aria-sort');
                        this.setDescription(true, 'ascending');
                        if (!this.tableComponent.disableDefaultSort) {
                            this.tableComponent.allowDataReload = false;
                            this.tableComponent.allowAnimationWhenReload = false;
                            this.tableComponent.originalDataChange.emit(cloneDeep(this.originData));
                            this.indexedSortedData.sort((a, b) => { return a._initialSortIndex > b._initialSortIndex ? 1 : -1; });
                            this.indexedSortedData.forEach((data, i) => {
                                data._previousSortIndex = data._nextSortIndex;
                                data._nextSortIndex = data._initialSortIndex;
                            });
                            this.tableComponent.displayDataChange.emit(this.indexedSortedData);
                            this.sortTableService.sortedData[this.tableId] = this.indexedSortedData;
                        }
                    });
                }
            });
            if (!this.tableComponent.disableDefaultSort) {
                cloneDeep(this.tableComponent.originalData).forEach((data, i) => {
                    this.indexedSortedData.push({
                        _initialSortIndex: i,
                        _previousSortIndex: i,
                        _nextSortIndex: i,
                        ...data
                    });
                });
            }
        }
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.initTableHead();
        });
    }
    ngOnDestroy() {
        this.translationSubscription$?.unsubscribe();
    }
    getOrderTranslation(key) {
        return this.localizationConfig.translation ? this.localizationConfig.getTranslation(key) : null;
    }
    getSortMessage(sortKey, order) {
        return this.localizationConfig.translation?.aria ? this.localizationConfig.translation.aria['tableSortMessage'].replaceAll('{sortKey}', sortKey).replaceAll('{order}', order) : null;
    }
    getUnSortMessage(sortKey) {
        return this.localizationConfig.translation?.aria ? this.localizationConfig.translation.aria['tableUnsortMessage'].replaceAll('{sortKey}', sortKey) : null;
    }
    initTableHead() {
        if (!this.sortKey) {
            return;
        }
        this.createIcon();
        this.createDescription();
        const head = this.element.nativeElement.querySelector('.header-container');
        head.classList.add('has-sorter');
        // head.setAttribute('tabindex', 0);
        head.setAttribute('role', 'button');
        head.setAttribute('type', 'button');
        // head.setAttribute('aria-describedby', 'table-sort-' + this.sorterId);
        this.render.listen(head, 'keydown', event => {
            if (event.keyCode === 13) {
                event.preventDefault();
                this.sort(event);
            }
        });
    }
    createIcon() {
        const container = this.element.nativeElement.querySelector('.header-container');
        const node = this.render.createElement('div');
        const sortupIcon = this.render.createElement('span');
        sortupIcon.classList.add('Appkit4-icon', 'icon-sorting-ascendant-fill', 'sort-table', 'animated', 'hidden');
        const defaultIcon = this.render.createElement('span');
        defaultIcon.classList.add('Appkit4-icon', 'icon-sorting-fill', 'sort-table', 'animated');
        node.appendChild(sortupIcon);
        node.appendChild(defaultIcon);
        // node.setAttribute('aria-hidden', 'true');
        node.setAttribute('tabindex', '0');
        node.classList.add('sort-icon-wrapper');
        node.setAttribute('type', 'button');
        node.setAttribute('role', 'button');
        node.setAttribute('aria-describedby', 'table-sort-' + this.sorterId);
        container.appendChild(node);
        if (this.slot === 'start') {
            container.classList.add('start-slot');
        }
        else {
            container.classList.add('end-slot');
        }
    }
    createDescription() {
        const container = this.element.nativeElement.querySelector('.header-container');
        const descriptionNode = this.render.createElement('span');
        descriptionNode.classList.add('ap-table-header-description-sr-only');
        descriptionNode.setAttribute('id', 'table-sort-' + this.sorterId);
        const order1 = this.sortFunc1 ? this.getOrderTranslation('custom') : this.getOrderTranslation('ascending');
        const text = this.getSortMessage(this.sortKey, order1);
        const nodeContent = document.createTextNode(text);
        descriptionNode.appendChild(nodeContent);
        container.appendChild(descriptionNode);
    }
    setDescription(sort, order) {
        const descriptionNode = this.element.nativeElement.querySelector('.ap-table-header-description-sr-only');
        if (descriptionNode) {
            descriptionNode.innerHTML = sort ? this.getSortMessage(this.sortKey, this.getOrderTranslation(order)) : this.getUnSortMessage(this.sortKey);
        }
    }
    sort(event) {
        if (!this.sortKey) {
            return;
        }
        this.tableComponent.onSort.emit({
            tableId: this.tableId,
            sortKey: this.sortKey,
            sortingPhase: this.sortFlag === 0 ? 1 : this.sortFlag === 2 ? 2 : 0
        });
        if (this.tableComponent.checkboxNodes.length && this.tableComponent.selectAllref) {
            this.tableComponent.selectAllref.instance.setCheckboxState({
                originEvent: event,
                checked: false
            });
        }
        const hoverHead = this.element.nativeElement.querySelector('.header-container');
        const defaultIcon = this.element.nativeElement.querySelector('.icon-sorting-fill');
        const sortupIcon = this.element.nativeElement.querySelector('.icon-sorting-ascendant-fill');
        const order1 = this.sortFunc1 ? 'custom' : 'ascending';
        const order2 = this.sortFunc2 ? 'custom' : 'descending';
        if (this.sortFlag === 1) { // back to the default state
            this.sortFlag = 0;
            this.animate(sortupIcon, 'rotateIn');
            setTimeout(() => {
                defaultIcon.classList.remove('hidden');
                sortupIcon.classList.add('hidden');
                hoverHead.classList.remove('head-sorted');
            }, 300);
            this.element.nativeElement.removeAttribute('aria-sort');
            this.setDescription(true, order1);
            if (!this.tableComponent.disableDefaultSort) {
                this.tableComponent.allowDataReload = false;
                this.tableComponent.originalDataChange.emit(cloneDeep(this.originData));
                this.indexedSortedData.sort((a, b) => { return a._initialSortIndex > b._initialSortIndex ? 1 : -1; });
                // reset index after each sorting
                this.indexedSortedData.forEach((data, i) => {
                    data._previousSortIndex = data._nextSortIndex;
                    data._nextSortIndex = data._initialSortIndex;
                });
                this.tableComponent.displayDataChange.emit(this.indexedSortedData);
                this.sortTableService.sortedData[this.tableId] = this.indexedSortedData;
            }
            // reload indexedSortedData in all directives
            this.sortTableService.sortEvent.next({ phase: 2, tableId: this.tableId });
        }
        else if (this.sortFlag === 0) { // 1st sorting phase
            this.sortFlag = 3;
            this.removeIconClass();
            this.animate(defaultIcon, 'rotateOut');
            defaultIcon.classList.add('sorted');
            sortupIcon.classList.add('sorted');
            hoverHead.classList.add('head-sorted');
            setTimeout(() => {
                defaultIcon.classList.add('hidden');
                sortupIcon.classList.remove('hidden');
            }, 300);
            this.setDescription(true, order2);
            let sortFunction;
            if (this.sortFunc1) {
                sortFunction = this.sortFunc1;
                this.element.nativeElement.removeAttribute('aria-sort');
            }
            else {
                sortFunction = this.defaultSortFunc.bind(this);
                this.element.nativeElement.setAttribute('aria-sort', 'ascending');
            }
            if (!this.tableComponent.disableDefaultSort) {
                this.sortedData.sort(sortFunction);
                this.tableComponent.allowDataReload = false;
                this.tableComponent.originalDataChange.emit(cloneDeep(this.sortedData));
                this.indexedSortedData.sort(sortFunction);
                // reset index after each sorting
                this.indexedSortedData.forEach((data, i) => {
                    data._previousSortIndex = data._nextSortIndex;
                    data._nextSortIndex = i;
                });
                this.tableComponent.displayDataChange.emit(this.indexedSortedData);
                this.sortTableService.sortedData[this.tableId] = this.indexedSortedData;
            }
            // set current sort directive to the first sorting phase,
            // set other sort directives to the default inactive state
            this.sortTableService.sortEvent.next({ phase: 1, tableId: this.tableId });
        }
        else if (this.sortFlag === 2) { // 2nd sorting phase
            this.sortFlag = 1;
            this.removeIconClass();
            this.animate(sortupIcon, 'rotateOut');
            defaultIcon.classList.add('hidden');
            sortupIcon.classList.remove('hidden');
            sortupIcon.classList.add('sorted');
            hoverHead.classList.add('head-sorted');
            setTimeout(() => {
                sortupIcon.classList.add('descendant');
            }, 300);
            this.setDescription(false);
            let sortFunction;
            if (this.sortFunc2) {
                sortFunction = this.sortFunc2;
                this.element.nativeElement.removeAttribute('aria-sort');
            }
            else {
                sortFunction = this.defaultSortFunc.bind(this);
                this.element.nativeElement.setAttribute('aria-sort', 'descending');
            }
            if (!this.tableComponent.disableDefaultSort) {
                this.sortedData.sort(sortFunction);
                this.tableComponent.allowDataReload = false;
                this.tableComponent.originalDataChange.emit(cloneDeep(this.sortedData));
                this.indexedSortedData.sort(sortFunction);
                // reset index after each sorting
                this.indexedSortedData.forEach((data, i) => {
                    data._previousSortIndex = data._nextSortIndex;
                    data._nextSortIndex = i;
                });
                this.tableComponent.displayDataChange.emit(this.indexedSortedData);
                this.sortTableService.sortedData[this.tableId] = this.indexedSortedData;
            }
            // reload indexedSortedData in all directives
            this.sortTableService.sortEvent.next({ phase: 2, tableId: this.tableId });
        }
    }
    removeIconClass() {
        let headers = this.element.nativeElement.parentNode.querySelectorAll('th');
        headers.forEach((element) => {
            const hoverHead = element.querySelector('.header-container');
            const defaultIcon = element.querySelector('.icon-sorting-fill');
            const sortupIcon = element.querySelector('.icon-sorting-ascendant-fill');
            if (sortupIcon) {
                sortupIcon.classList.remove('sorted', 'descendant');
                sortupIcon.classList.add('hidden');
            }
            if (defaultIcon) {
                defaultIcon.classList.remove('sorted');
                defaultIcon.classList.remove('hidden');
            }
            if (hoverHead) {
                hoverHead.classList.remove('head-sorted');
            }
            element.removeAttribute('aria-sort');
        });
    }
    defaultSortFunc(itemA, itemB) {
        const a = itemA[this.sortKey], b = itemB[this.sortKey];
        if (!(a || a === 0)) {
            return 1;
        }
        if (!(b || b === 0)) {
            return -1;
        }
        if (a === b) {
            return 0;
        }
        if (this.sortFlag === 1) {
            return a < b ? 1 : -1;
        }
        else {
            return a > b ? 1 : -1;
        }
    }
    animate(element, animation) {
        element.classList.add(animation);
        setTimeout(() => {
            element.classList.remove(animation);
        }, 300);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: SortTableDirective, deps: [{ token: i0.ElementRef }, { token: SortTableService }, { token: TableComponent }, { token: i0.Renderer2 }, { token: i1.LocalizationConfig }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.11", type: SortTableDirective, selector: "[ap-sort]", inputs: { sortKey: "sortKey", sortFunc1: "sortFunc1", sortFunc2: "sortFunc2", slot: "slot" }, host: { listeners: { "click": "sort($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: SortTableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ap-sort]',
                    host: {
                        '(click)': 'sort($event)'
                    }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: SortTableService }, { type: TableComponent }, { type: i0.Renderer2 }, { type: i1.LocalizationConfig }], propDecorators: { sortKey: [{
                type: Input
            }], sortFunc1: [{
                type: Input
            }], sortFunc2: [{
                type: Input
            }], slot: [{
                type: Input
            }] } });
class TableModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: TableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.11", ngImport: i0, type: TableModule, declarations: [TableComponent, SortTableDirective], imports: [CommonModule,
            CheckboxModule,
            PaginationModule], exports: [TableComponent, SortTableDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: TableModule, providers: [SortTableService], imports: [CommonModule,
            CheckboxModule,
            PaginationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.11", ngImport: i0, type: TableModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CheckboxModule,
                        PaginationModule
                    ],
                    exports: [TableComponent, SortTableDirective],
                    declarations: [TableComponent, SortTableDirective],
                    providers: [SortTableService]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SortTableDirective, SortTableService, TableComponent, TableModule };
